import type { PriceTick } from '@/lib/instrumentTick';
import { cssVar } from '@/lib/theme/cssVar';
import {
  computeLabelPrices,
  decimalsFor,
  easeInOutCubic,
  interpolateTicks,
  minSpanFor,
  padPriceRange,
  springStep,
} from './chartMath';

/**
 * Self-contained canvas renderer for the live price chart — a 1:1 port of the
 * Polymarket 5-minute chart recreation (the teardown artifact), swapped in
 * place of the previous lightweight-charts pipeline.
 *
 * Everything is drawn per-frame from a small rolling window of raw ticks:
 * - x maps wall-clock time onto a sliding window ({@link windowSecFor}:
 *   WINDOW_MIN_SEC on phones up to WINDOW_MAX_SEC on desktop panes)
 *   with a fixed right gap, so the line scrolls continuously;
 * - the head is a critically damped spring tracking the latest tick price
 *   (HEAD_TAU_MS), so any feed cadence — 1 Hz or a 20/s burst — glides
 *   continuously instead of stepping or rubber-banding;
 * - the y-window is the drawn range clamped to a per-asset minimum span,
 *   padded ±20%, plus 10% margins — no user zoom exists;
 * - price labels are tracked by price key and animate (slide 100ms, fade
 *   150ms) instead of teleporting; hairlines + edge fades included;
 * - the Target line clamps to the pane edge with blinking chevrons when the
 *   strike leaves the window; the frozen view adds a solid Final Price line.
 *
 * Clock discipline: all x-math runs on the FEED clock. Each tick refines a
 * smoothed offset between feed time and client time, so a skewed local clock
 * can never pin the head in place (the failure mode of stamping server-fed
 * series with client time).
 *
 * Colors resolve from the app's design tokens per theme (see resolvePalette);
 * typography is the app font. Behavior constants remain reference-faithful.
 */

// ===== Tuning (verbatim from the artifact / reference implementation) =====
// Visible sliding window — responsive to pane width: phones get a tight,
// fast-moving WINDOW_MIN_SEC window; the span grows linearly with width up to WINDOW_MAX_SEC on
// desktop panes. (The reference's bar-based window drifts ~35s → ~115s the
// longer its page sits open; ours is true-time and stays put per width.)
const WINDOW_MIN_SEC = 15;
const WINDOW_MAX_SEC = 25;
const WINDOW_MIN_AT_PX = 480; // canvas width at (or below) which the window is WINDOW_MIN_SEC
// The app's desktop layout caps the chart column at ~640px, so the ramp tops
// out there — desktop panes get the full WINDOW_MAX_SEC.
const WINDOW_MAX_AT_PX = 640;

/** Visible window span (seconds) for a given canvas width. */
function windowSecFor(canvasW: number): number {
  const t = (canvasW - WINDOW_MIN_AT_PX) / (WINDOW_MAX_AT_PX - WINDOW_MIN_AT_PX);
  return WINDOW_MIN_SEC + Math.min(Math.max(t, 0), 1) * (WINDOW_MAX_SEC - WINDOW_MIN_SEC);
}
const RIGHT_GAP_PX = 66; // breathing room between the live head and the right edge
// Trail density: the seed is interpolated to this cadence, and live frames
// append at the display rate (~60/s) — matching the reference chart's
// interpolated series (hover gaps ≈ 16ms).
const TRAIL_POINTS_PER_SEC = 60;
// Flat-fill trail gaps longer than this (hidden tab / stalled feed) so the
// resumed line doesn't ramp across the gap.
const TRAIL_GAP_FILL_SEC = 2;
const AXIS_H_PX = 26; // time-axis strip height
// Head tracking — a critically damped spring toward the latest tick price.
// The reference restarts a fixed 1000ms easeInOutCubic per tick (Polymarket's
// `ANIMATION_DURATION`), which is only sound on their metronomic 1 Hz feed,
// where the tween completes exactly as the next tick lands. Our Plaee
// firehose is bursty (measured 2026-07-13 on sandbox BTC: ~19 ticks/s, median
// gap 0ms, lulls up to ~1s) — restarting an ease-IN curve every ~50ms pins
// the head at the curve's zero-slope start, so it tracked ~1%/s through
// bursts and lurched to catch up in every lull. The spring keeps the
// reference feel on an isolated tick (zero initial slope, soft landing,
// settled in ~1s) but carries velocity across retargets, so burst ticks
// steer the glide instead of resetting it. τ = 210ms ⇒ ~95% settled at ~1s
// (4.7·τ), matching the reference's glide time.
const HEAD_TAU_MS = 210;
const LINE_WIDTH = 2;
// Desired price-label count scales with pane height, calibrated to hit the
// reference's density: they hardcode a target of 8 on their fixed 260px pane
// (~32px/label), and the ceiling-biased step snapping makes the target a
// maximum, so actual counts land at 5-8.
const AXIS_LABEL_EVERY_PX = 34;
const AXIS_MAX_TICKS = 8;
const AXIS_MIN_TICKS = 3;
// Clearance between the live head and the price-label column.
const HEAD_LABEL_CLEARANCE_PX = 24;
// Time constant (ms) for easing the applied y-scale toward its target. The
// target range jumps whenever an extreme enters or leaves the window; easing
// the applied range turns that snap into a glide (the animated labels follow).
const SCALE_SMOOTH_TAU_MS = 120;
// Candidate time-axis steps; the smallest one keeping labels ≥ MIN_TIME_LABEL_PX
// apart wins (the reference uses a fixed 10s on a much wider pane).
const TIME_STEP_CANDIDATES_SEC = [5, 10, 15, 30, 60, 120, 300];
const MIN_TIME_LABEL_PX = 88;

// Label animation: position slide, fade in/out, recompute throttle, edge fades.
const LABEL_POS_MS = 100;
const LABEL_FADE_MS = 150;
const LABEL_RECOMPUTE_MS = 100;
const EDGE_MASK_PX = 30;

// Pulsing head dot (the "live" heartbeat) and the clamped-target chevron blink.
const PULSE_CYCLE_MS = 1300;
const CHEVRON_CYCLE_MS = 1500;
const CHEVRON_STAGGER_MS = 400;

// Palette — resolved from the app's design tokens (see index.css), so the
// chart re-themes with [data-theme="dark"]. Fallbacks are the reference
// (Polymarket-style) values, used when a token is missing (e.g. unit tests).
interface ChartPalette {
  /** Single accent (dot, pulse, dashed current-price, tooltip bullet). */
  accent: string;
  /** Vertical line-stroke gradient stops (top → bottom), the app's brand purple. */
  lineStops: [string, string, string];
  /** Area fill under the line (top → bottom, alpha built into the tokens). */
  fillTop: string;
  fillBottom: string;
  label: string;
  target: string;
  chipBg: string;
  chipInk: string;
  finalUp: string;
  finalDown: string;
  tooltipInk: string;
  surface: string;
}

function resolvePalette(): ChartPalette {
  const isDark = typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark';
  const tok = (name: string, fallback: string) => cssVar(name) || fallback;
  return {
    accent: tok('--purple', '#721cb7'),
    lineStops: [tok('--chart-line-1', '#9d4edd'), tok('--chart-line-2', '#7b2cbf'), tok('--chart-line-3', '#6506ab')],
    fillTop: tok('--chart-fill-top', 'rgba(178, 107, 240, 0.22)'),
    fillBottom: tok('--chart-fill-bottom', 'rgba(178, 107, 240, 0)'),
    label: tok('--muted', '#77808d'),
    target: tok('--ink-muted', '#6b7280'),
    // Chip surface: the reference's annotation slate, per theme (no app token).
    chipBg: isDark ? '#586879' : '#939aa5',
    chipInk: '#ffffff',
    finalUp: tok('--success', '#16a34a'),
    finalDown: tok('--danger', '#dc2626'),
    tooltipInk: tok('--ink', '#242a33'),
    surface: tok('--surface', '#ffffff'),
  };
}
// All chart text uses the app font (--font-sans), same as the other charts.
const FONT_LABEL = "400 11px 'Outfit', sans-serif"; // y-axis price labels
const FONT_AXIS = "400 10.5px 'Outfit', sans-serif"; // time axis
const FONT_CHIP = "500 11px 'Outfit', sans-serif"; // Target / Final chips
// Hover inspector: prominent price over a muted full date/time, no chip box.
const FONT_TOOLTIP_PRICE = "600 14px 'Outfit', sans-serif";
const FONT_TOOLTIP_DATE = "400 10.5px 'Outfit', sans-serif";
// ==========================================================================

/** Configuration for one engine instance. */
export interface LiveChartEngineConfig {
  /** Asset symbol driving the min-span clamp, tick floor and label decimals. */
  symbol: string | null;
  /** Anchor price for the relative min-span fallback (unknown symbols). */
  anchorPrice: number;
  /** Strike / "price to beat"; `null` hides the Target line. Read every frame. */
  getTargetPrice: () => number | null;
  /**
   * Settled price + chip label for the frozen past view; `null` hides the
   * Final Price line. Read every frame.
   */
  getFinalPrice: () => { price: number; label: string } | null;
  /** Frozen mode: static full-span rendering, no head motion, no pulse. */
  frozen: boolean;
}

interface TrackedLabel {
  price: number;
  curY: number;
  startY: number;
  targetY: number;
  posT0: number;
  opT0: number;
  opacity: number;
  isNew: boolean;
  removing: boolean;
}

function alpha(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? [...h].map((c) => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

// Axis/hover times render in **Eastern Time**, matching every other clock on
// the snake surfaces (the "…AM ET" window subtitle, the timeline-strip pills)
// — a local-timezone axis under an ET header reads as two different markets.
// Formatters are cached at module level: `format()` runs per axis label per
// frame, and constructing Intl.DateTimeFormat is the expensive part.
const ET_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
});
const ET_DATE = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });

/** "Jul 13, 3:56:54 PM" (ET) — the hover inspector's date line. */
function fmtDateTime(ms: number): string {
  return `${ET_DATE.format(ms)}, ${fmtClock(ms)}`;
}

/** "3:56:54 PM" (ET) — time-axis labels. */
function fmtClock(ms: number): string {
  // ICU emits a narrow no-break space before AM/PM; normalise for canvas text.
  return ET_CLOCK.format(ms).replace('\u202f', ' ');
}

export class LiveChartEngine {
  private readonly canvas: HTMLCanvasElement;
  private readonly cfg: LiveChartEngineConfig;
  private readonly decimals: number;
  private readonly reducedMotion: boolean;

  /**
   * The line itself: a dense, time-ascending trail (~60 points/second). The
   * seed is eased-interpolated history; live, every rendered frame appends the
   * current head position — so the path the head traces IS what stays on the
   * line (it never re-shapes behind the head), and hover snapping has ~16ms
   * gaps, matching the reference chart's interpolated series.
   */
  private dense: PriceTick[] = [];
  /** Head spring state: drawn price + velocity (per ms), tracking headTarget. */
  private headPrice = 0;
  private headVelocity = 0;
  private headTarget = 0;
  /** Smoothed offset (ms) between the feed clock and the client clock. */
  private clockOffsetMs: number | null = null;
  /** Newest feed timestamp seen — stale (out-of-order) ticks are dropped. */
  private lastTickTimeSec = 0;
  private seeded = false;

  // Animated price labels, tracked by price key.
  private readonly labels = new Map<string, TrackedLabel>();
  private lastLabelCompute = 0;

  // Clamped-target chevron animation epoch (restarts when clamp state changes).
  private targetAnimT0 = 0;
  private targetWasOff = false;

  // Hover state (marker + canvas tooltip chip). Null when the pointer is away.
  private mouse: { x: number; y: number } | null = null;

  // Applied (smoothed) y-scale, eased toward the per-frame target range.
  private scaleLo: number | null = null;
  private scaleHi: number | null = null;
  private lastRenderMs: number | null = null;

  private rafId: number | null = null;
  private destroyed = false;

  /** Design-token palette; re-resolved when [data-theme] flips. */
  private pal = resolvePalette();
  private themeObserver: MutationObserver | null = null;

  constructor(canvas: HTMLCanvasElement, cfg: LiveChartEngineConfig) {
    this.canvas = canvas;
    this.cfg = cfg;
    this.decimals = decimalsFor(cfg.symbol);
    this.reducedMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mouseleave', this.onMouseLeave);
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => {
        this.pal = resolvePalette();
        this.cachedMask = undefined; // backdrop color changed with the theme
      });
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }
    this.rafId = requestAnimationFrame(this.renderLoop);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('mouseleave', this.onMouseLeave);
    this.themeObserver?.disconnect();
    this.themeObserver = null;
  }

  /** Seed the window from history (mock burst, synthetic walk, or frozen candles). */
  seed(ticks: readonly PriceTick[]): void {
    const sorted = ticks.slice().sort((a, b) => a.time - b.time);
    // Live: keep only what can ever be visible (time-based, so any history
    // cadence works — 1s ticks or coarser snapshots). Frozen keeps everything.
    let raw = sorted;
    if (!this.cfg.frozen && sorted.length > 0) {
      const tailTime = sorted[sorted.length - 1].time;
      raw = sorted.filter((p) => p.time >= tailTime - (WINDOW_MAX_SEC + 2));
    }
    // Densify to the live trail's cadence so seed and live segments share the
    // same curve character and hover density.
    this.dense = interpolateTicks(raw, TRAIL_POINTS_PER_SEC);
    const tail = this.dense[this.dense.length - 1];
    if (tail) {
      this.headPrice = tail.price;
      this.headVelocity = 0;
      this.headTarget = tail.price;
      this.lastTickTimeSec = tail.time;
      this.calibrateClock(tail.time);
    }
    this.seeded = true;
  }

  /** Feed one live tick: retarget the head spring (the per-frame render advances it). */
  pushTick(tick: PriceTick): void {
    if (!this.seeded || this.cfg.frozen) return;
    // The feed delivers out-of-order sendingTimes, especially at high tick
    // rates — retargeting toward a stale price flickers the head backward, so
    // drop ticks strictly OLDER than the newest seen. Equal timestamps pass:
    // feeds with coarse stamps can carry a fresh price on the same instant,
    // and dropping those loses real movement.
    if (tick.time < this.lastTickTimeSec) return;
    this.lastTickTimeSec = tick.time;
    this.calibrateClock(tick.time);
    // Retargeting is free: the spring's position and velocity are continuous,
    // so the head keeps its momentum and bends toward the new price.
    this.headTarget = tick.price;
  }

  /**
   * Persist the head's current position into the trail (called every rendered
   * frame). Long gaps (hidden tab, stalled feed) are flat-filled at 1s cadence
   * first so resuming doesn't draw one long ramp across the gap.
   */
  private appendHeadToTrail(nowMs: number, headPrice: number): void {
    const nowSec = nowMs / 1000;
    const last = this.dense[this.dense.length - 1];
    if (last) {
      if (nowSec <= last.time) return; // duplicate/backwards frame — skip
      if (nowSec - last.time > TRAIL_GAP_FILL_SEC) {
        const fillFrom = Math.max(Math.floor(last.time) + 1, Math.ceil(nowSec - WINDOW_MAX_SEC - 1));
        for (let t = fillFrom; t < nowSec; t += 1) {
          if (t > last.time) this.dense.push({ time: t, price: last.price });
        }
      }
    }
    this.dense.push({ time: nowSec, price: headPrice });
    // Prune behind the visible window (small slack so the line exits the left
    // edge cleanly).
    const cutoff = nowSec - (WINDOW_MAX_SEC + 5);
    let drop = 0;
    while (drop < this.dense.length - 1 && this.dense[drop].time < cutoff) drop++;
    if (drop > 0) this.dense.splice(0, drop);
  }

  // --- clock ---------------------------------------------------------------

  /**
   * Track the offset between feed timestamps and the local clock (smoothed to
   * absorb jitter). All rendering time-math uses the feed clock, so server ↔
   * client skew shifts the whole chart uniformly instead of pinning the head.
   */
  private calibrateClock(feedTimeSec: number): void {
    const target = feedTimeSec * 1000 - Date.now();
    this.clockOffsetMs = this.clockOffsetMs === null ? target : this.clockOffsetMs + (target - this.clockOffsetMs) * 0.1;
  }

  private feedNowMs(): number {
    return Date.now() + (this.clockOffsetMs ?? 0);
  }

  // --- head spring -----------------------------------------------------------

  /** Advance the head spring by one frame (`dtMs`) toward the latest tick price. */
  private advanceHead(dtMs: number): void {
    if (this.reducedMotion) {
      this.headPrice = this.headTarget;
      this.headVelocity = 0;
      return;
    }
    const next = springStep(this.headPrice, this.headVelocity, this.headTarget, dtMs, HEAD_TAU_MS);
    this.headPrice = next.value;
    this.headVelocity = next.velocity;
  }

  // --- events ----------------------------------------------------------------

  private readonly onMouseMove = (e: MouseEvent): void => {
    const r = this.canvas.getBoundingClientRect();
    this.mouse = { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  private readonly onMouseLeave = (): void => {
    this.mouse = null;
  };

  // --- render ----------------------------------------------------------------

  private readonly renderLoop = (): void => {
    if (this.destroyed) return;
    try {
      this.render();
    } catch (err) {
      // A single bad frame must never kill the renderer.
      if (import.meta.env.DEV) console.warn('[chart] frame skipped', err);
    }
    this.rafId = requestAnimationFrame(this.renderLoop);
  };

  private fitCanvas(): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
    const cv = this.canvas;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = cv.clientWidth;
    const h = cv.clientHeight;
    if (w === 0 || h === 0) return null;
    if (cv.width !== w * dpr || cv.height !== h * dpr) {
      cv.width = w * dpr;
      cv.height = h * dpr;
    }
    const ctx = cv.getContext('2d');
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    return { ctx, w, h };
  }

  private fmtPrice(v: number, minDecimals = 0): string {
    const decimals = Math.max(this.decimals, minDecimals);
    return `$${v.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })}`;
  }

  private render(): void {
    const fit = this.fitCanvas();
    if (!fit || !this.seeded || this.dense.length === 0) return;
    const { ctx, w, h } = fit;
    ctx.clearRect(0, 0, w, h);

    const plotH = h - AXIS_H_PX;
    const nowMs = this.feedNowMs();

    // Frame delta (clamped so a background-tab resume doesn't integrate one
    // giant step) — drives both the head spring and the y-scale smoothing.
    const frameNow = performance.now();
    const dtMs = this.lastRenderMs === null ? 16 : Math.min(frameNow - this.lastRenderMs, 100);
    this.lastRenderMs = frameNow;

    // Advance the head spring, persist its position into the trail, then draw
    // the trail itself — the line IS the recorded path (nothing re-shapes
    // behind the head).
    if (!this.cfg.frozen) {
      this.advanceHead(dtMs);
      this.appendHeadToTrail(nowMs, this.headPrice);
    }
    const pts: readonly PriceTick[] = this.dense;
    const head = pts[pts.length - 1];

    // y-mapping first (the x right-gap depends on the label column width):
    // data range → min-span clamp → ±20% pad → 10% margins.
    //
    // The range covers only what is VISIBLE: points inside the sliding window
    // plus the interpolated value where the line crosses the left edge. Points
    // that scrolled off-screen must not hold the scale — and because the edge
    // crossing is interpolated, the target range moves *continuously* as an
    // extreme slides out instead of jumping when it exits (the once-per-second
    // "rescale for no visible reason" lurch).
    const winSec = windowSecFor(w);
    const leftEdgeSec = this.cfg.frozen ? -Infinity : nowMs / 1000 - winSec;
    let mn = Infinity;
    let mx = -Infinity;
    let prevPt: PriceTick | null = null;
    for (const p of pts) {
      if (p.time >= leftEdgeSec) {
        if (prevPt && prevPt.time < leftEdgeSec && p.time > prevPt.time) {
          const f = (leftEdgeSec - prevPt.time) / (p.time - prevPt.time);
          const edgeV = prevPt.price + (p.price - prevPt.price) * f;
          if (edgeV < mn) mn = edgeV;
          if (edgeV > mx) mx = edgeV;
        }
        if (p.price < mn) mn = p.price;
        if (p.price > mx) mx = p.price;
      }
      prevPt = p;
    }
    if (!Number.isFinite(mn)) {
      mn = head.price;
      mx = head.price;
    }
    const padded = padPriceRange(mn, mx, minSpanFor(this.cfg.symbol, this.cfg.anchorPrice));
    const margin = (padded.maxValue - padded.minValue) * 0.1;
    const targetLo = padded.minValue - margin;
    const targetHi = padded.maxValue + margin;

    // Scale application, per side: EXPANSION is instant — the reference
    // re-fits every frame, so its head never outruns the scale on a spike —
    // while SHRINK (an extreme scrolling out) eases, turning the only jarring
    // rescale into a glide.
    if (this.scaleLo === null || this.scaleHi === null || this.cfg.frozen || this.reducedMotion) {
      this.scaleLo = targetLo;
      this.scaleHi = targetHi;
    } else {
      const k = 1 - Math.exp(-dtMs / SCALE_SMOOTH_TAU_MS);
      this.scaleLo = targetLo < this.scaleLo ? targetLo : this.scaleLo + (targetLo - this.scaleLo) * k;
      this.scaleHi = targetHi > this.scaleHi ? targetHi : this.scaleHi + (targetHi - this.scaleHi) * k;
    }
    const lo = this.scaleLo;
    const hi = this.scaleHi;
    const y = (v: number) => plotH - ((v - lo) / (hi - lo)) * plotH;

    // Width of the in-pane label column (widest range label + 10px edge inset),
    // quantized to 8px steps so a digit-count change can't jitter the x-gap.
    ctx.font = FONT_LABEL;
    const labelColW =
      Math.ceil((Math.max(ctx.measureText(this.fmtPrice(lo)).width, ctx.measureText(this.fmtPrice(hi)).width) + 10) / 8) * 8;

    // x-mapping: live = sliding window ending at "now", with the head held
    // HEAD_LABEL_CLEARANCE_PX left of the label column; frozen = the full
    // settled span edge-to-edge.
    let x: (tSec: number) => number;
    let axisPxPerSec: number;
    if (this.cfg.frozen) {
      const t0 = this.dense[0].time;
      const t1 = this.dense[this.dense.length - 1].time;
      const span = Math.max(t1 - t0, 1);
      x = (t) => ((t - t0) / span) * w;
      axisPxPerSec = w / span;
    } else {
      const rightGap = Math.max(RIGHT_GAP_PX, labelColW + HEAD_LABEL_CLEARANCE_PX);
      const pxPerSec = (w - rightGap) / winSec;
      x = (t) => w - rightGap - (nowMs / 1000 - t) * pxPerSec;
      axisPxPerSec = pxPerSec;
    }

    // ----- area fill + line. The trail is already the eased curve at ~60
    // points/sec (sub-pixel spacing), so straight segments render identically
    // to a spline; points are decimated to ~1px columns to keep the per-frame
    // path cheap. Sparse stretches (frozen seeds with second-gaps) still get
    // midpoint-bezier smoothing.
    const drawPts: { px: number; py: number }[] = [];
    let lastEmittedX = -Infinity;
    for (let i = 0; i < pts.length; i++) {
      const px = x(pts[i].time);
      if (i === pts.length - 1 || px - lastEmittedX >= 1) {
        drawPts.push({ px, py: y(pts[i].price) });
        lastEmittedX = px;
      }
    }
    ctx.beginPath();
    drawPts.forEach((p, i) => {
      if (i === 0) {
        ctx.moveTo(p.px, p.py);
      } else {
        const pr = drawPts[i - 1];
        if (p.px - pr.px > 4) {
          const mid = (pr.px + p.px) / 2;
          ctx.bezierCurveTo(mid, pr.py, mid, p.py, p.px, p.py);
        } else {
          ctx.lineTo(p.px, p.py);
        }
      }
    });
    const headX = x(head.time);
    const headY = y(head.price);
    const strokeGrad = ctx.createLinearGradient(0, 0, 0, plotH);
    strokeGrad.addColorStop(0, this.pal.lineStops[0]);
    strokeGrad.addColorStop(0.5, this.pal.lineStops[1]);
    strokeGrad.addColorStop(1, this.pal.lineStops[2]);
    ctx.strokeStyle = strokeGrad;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.lineTo(headX, plotH);
    ctx.lineTo(x(pts[0].time), plotH);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, 0, 0, plotH);
    grad.addColorStop(0, this.pal.fillTop);
    grad.addColorStop(1, this.pal.fillBottom);
    ctx.fillStyle = grad;
    ctx.fill();

    // ----- dashed current-price line (live only)
    if (!this.cfg.frozen) {
      ctx.strokeStyle = this.pal.accent;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.setLineDash([1.5, 4]);
      ctx.beginPath();
      ctx.moveTo(0, Math.round(headY) + 0.5);
      ctx.lineTo(w, Math.round(headY) + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // ----- animated overlay price labels
    this.drawLabels(ctx, w, plotH, lo, hi, y);

    // ----- Target line + chip (clamps with blinking chevrons when off-window)
    const target = this.cfg.getTargetPrice();
    if (target != null && Number.isFinite(target)) {
      const off = target > hi || target < lo;
      if (off !== this.targetWasOff) {
        this.targetAnimT0 = performance.now();
        this.targetWasOff = off;
      }
      this.drawPriceLine(ctx, w, plotH, target, lo, hi, y, {
        color: this.pal.target,
        dash: [6, 6],
        label: 'Target',
        chevrons: true,
      });
    }

    // ----- Final Price line (frozen view only)
    const final = this.cfg.getFinalPrice();
    if (final && Number.isFinite(final.price)) {
      const won = target == null || final.price >= target;
      this.drawPriceLine(ctx, w, plotH, final.price, lo, hi, y, {
        color: won ? this.pal.finalUp : this.pal.finalDown,
        dash: [],
        label: final.label,
        chevrons: false,
      });
    }

    // ----- pulsing head dot (live only)
    if (!this.cfg.frozen) {
      if (!this.reducedMotion) {
        const cyc = (performance.now() % PULSE_CYCLE_MS) / PULSE_CYCLE_MS;
        ctx.beginPath();
        ctx.arc(headX, headY, 4 + cyc * 12, 0, 7);
        ctx.strokeStyle = this.pal.accent;
        ctx.globalAlpha = 0.5 * (1 - cyc);
        ctx.lineWidth = 1.6;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.beginPath();
      ctx.arc(headX, headY, 4, 0, 7);
      ctx.fillStyle = this.pal.accent;
      ctx.fill();
      ctx.strokeStyle = this.pal.surface;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // ----- time axis
    ctx.strokeStyle = this.pal.label;
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, plotH + 0.5);
    ctx.lineTo(w, plotH + 0.5);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.fillStyle = this.pal.label;
    ctx.font = FONT_AXIS;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const axisStartSec = this.cfg.frozen ? this.dense[0].time : nowMs / 1000 - winSec;
    const axisEndSec = this.cfg.frozen ? this.dense[this.dense.length - 1].time : nowMs / 1000;
    const stepSec =
      TIME_STEP_CANDIDATES_SEC.find((s) => s * axisPxPerSec >= MIN_TIME_LABEL_PX) ??
      TIME_STEP_CANDIDATES_SEC[TIME_STEP_CANDIDATES_SEC.length - 1];
    for (let t = Math.ceil(axisStartSec / stepSec) * stepSec; t <= axisEndSec; t += stepSec) {
      const px = x(t);
      if (px > 34 && px < w - 60) ctx.fillText(fmtClock(t * 1000), px, plotH + 7);
    }

    // ----- hover: crosshair + open inspector header (no boxy chip).
    // A full-height hairline marks the inspected instant, a solid dot sits on
    // the line, and a two-line header floats at the top of the pane following
    // the cursor: a bullet in the line colour + the price in strong ink, with
    // the full date/time in muted ink beneath.
    if (this.mouse && this.mouse.y <= plotH) {
      let best: PriceTick | null = null;
      let bd = Infinity;
      for (const p of pts) {
        const d = Math.abs(x(p.time) - this.mouse.x);
        if (d < bd) {
          bd = d;
          best = p;
        }
      }
      if (best && bd < 40) {
        const bx = x(best.time);
        const by = y(best.price);

        // Crosshair hairline through the inspected instant.
        ctx.strokeStyle = alpha(this.pal.tooltipInk, 0.22);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(Math.round(bx) + 0.5, 0);
        ctx.lineTo(Math.round(bx) + 0.5, plotH);
        ctx.stroke();

        // Solid dot on the line (same treatment as the live head).
        ctx.beginPath();
        ctx.arc(bx, by, 4.5, 0, 7);
        ctx.fillStyle = this.pal.accent;
        ctx.fill();
        ctx.strokeStyle = this.pal.surface;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Header block, centred on the crosshair and clamped to the pane.
        // Cent precision in the inspector (axis labels keep per-asset decimals).
        const priceText = this.fmtPrice(best.price, 2);
        const dateText = fmtDateTime(best.time * 1000);
        ctx.font = FONT_TOOLTIP_PRICE;
        const bulletR = 3.5;
        const bulletGap = 7;
        const priceW = ctx.measureText(priceText).width + bulletR * 2 + bulletGap;
        ctx.font = FONT_TOOLTIP_DATE;
        const dateW = ctx.measureText(dateText).width;
        const blockW = Math.max(priceW, dateW);
        const cxBlock = Math.max(4 + blockW / 2, Math.min(bx, w - blockW / 2 - 4));

        ctx.textBaseline = 'middle';
        const priceY = 15;
        const dateY = 32;
        // price row: coloured bullet + price in strong ink
        const priceLeft = cxBlock - priceW / 2;
        ctx.beginPath();
        ctx.arc(priceLeft + bulletR, priceY, bulletR, 0, 7);
        ctx.fillStyle = this.pal.accent;
        ctx.fill();
        ctx.font = FONT_TOOLTIP_PRICE;
        ctx.textAlign = 'left';
        ctx.fillStyle = this.pal.tooltipInk;
        ctx.fillText(priceText, priceLeft + bulletR * 2 + bulletGap, priceY);
        // date row, muted, centred under the price
        ctx.font = FONT_TOOLTIP_DATE;
        ctx.textAlign = 'center';
        ctx.fillStyle = this.pal.label;
        ctx.fillText(dateText, cxBlock, dateY);
      }
    }
  }

  /** Animated in-pane price labels (tracked by price key; slide + cross-fade). */
  private drawLabels(
    ctx: CanvasRenderingContext2D,
    w: number,
    plotH: number,
    lo: number,
    hi: number,
    y: (v: number) => number,
  ): void {
    const now = performance.now();
    if (now - this.lastLabelCompute > LABEL_RECOMPUTE_MS) {
      this.lastLabelCompute = now;
      const targetTicks = Math.min(AXIS_MAX_TICKS, Math.max(AXIS_MIN_TICKS, Math.floor(plotH / AXIS_LABEL_EVERY_PX)));
      const prices = computeLabelPrices(lo, hi, targetTicks, this.decimals);
      const live = new Set<string>();
      for (const price of prices) {
        const py = y(price);
        if (py < 0 || py > plotH) continue;
        const key = price.toFixed(this.decimals);
        live.add(key);
        const t = this.labels.get(key);
        if (t && !t.removing) {
          if (Math.abs(t.targetY - py) > 2) {
            t.startY = t.curY;
            t.targetY = py;
            t.posT0 = now;
          }
        } else if (!t) {
          this.labels.set(key, {
            price,
            curY: py,
            startY: py,
            targetY: py,
            posT0: now,
            opT0: now,
            opacity: 0,
            isNew: true,
            removing: false,
          });
        }
      }
      for (const [key, t] of this.labels) {
        if (!live.has(key) && !t.removing) {
          t.removing = true;
          t.opT0 = now;
        }
      }
    }

    const dead: string[] = [];
    for (const [key, t] of this.labels) {
      const posP = Math.min((now - t.posT0) / LABEL_POS_MS, 1);
      t.curY = t.startY + (t.targetY - t.startY) * easeInOutCubic(posP);
      const opElapsed = now - t.opT0;
      if (t.isNew) {
        t.opacity = Math.min(opElapsed / LABEL_FADE_MS, 1);
        if (t.opacity >= 1) t.isNew = false;
      } else if (t.removing) {
        t.opacity = Math.max(1 - opElapsed / LABEL_FADE_MS, 0);
        if (t.opacity <= 0) dead.push(key);
      } else {
        t.opacity = 1;
      }
    }
    for (const key of dead) this.labels.delete(key);
    if (this.labels.size === 0) return;

    ctx.save();
    ctx.font = FONT_LABEL;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    let maxW = 0;
    for (const t of this.labels.values()) {
      maxW = Math.max(maxW, ctx.measureText(this.fmtPrice(t.price)).width);
    }
    const labelX = w - 10 - maxW;

    for (const t of this.labels.values()) {
      if (t.opacity <= 0.01) continue;
      const py = Math.round(t.curY) + 0.5;
      ctx.globalAlpha = 0.15 * t.opacity;
      ctx.strokeStyle = this.pal.label;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, py);
      ctx.lineTo(labelX - 6, py);
      ctx.stroke();
      ctx.globalAlpha = t.opacity;
      ctx.fillStyle = this.pal.label;
      ctx.fillText(this.fmtPrice(t.price), labelX, t.curY);
      ctx.globalAlpha = 1;
    }

    // Edge fades over the label column, using the element's backdrop color.
    const mask = this.maskColor();
    if (mask) {
      const gTop = ctx.createLinearGradient(0, 0, 0, EDGE_MASK_PX);
      gTop.addColorStop(0, mask.solid);
      gTop.addColorStop(1, mask.clear);
      ctx.fillStyle = gTop;
      ctx.fillRect(labelX - 4, 0, maxW + 14, EDGE_MASK_PX);
      const gBot = ctx.createLinearGradient(0, plotH - EDGE_MASK_PX, 0, plotH);
      gBot.addColorStop(0, mask.clear);
      gBot.addColorStop(1, mask.solid);
      ctx.fillStyle = gBot;
      ctx.fillRect(labelX - 4, plotH - EDGE_MASK_PX, maxW + 14, EDGE_MASK_PX);
    }
    ctx.restore();
  }

  private cachedMask: { solid: string; clear: string } | null | undefined;

  /** Resolve the canvas backdrop color once (for the label edge fades). */
  private maskColor(): { solid: string; clear: string } | null {
    if (this.cachedMask !== undefined) return this.cachedMask;
    let el: HTMLElement | null = this.canvas;
    let bg = '';
    while (el) {
      bg = getComputedStyle(el).backgroundColor;
      if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') break;
      el = el.parentElement;
    }
    if (!bg || bg === 'rgba(0, 0, 0, 0)' || bg === 'transparent') {
      this.cachedMask = null;
      return null;
    }
    const nums = bg.replace(/[^\d.,]/g, '').split(',');
    this.cachedMask = {
      solid: bg,
      clear: `rgba(${nums[0] ?? 0},${nums[1] ?? 0},${nums[2] ?? 0},0)`,
    };
    return this.cachedMask;
  }

  /** Horizontal price line + right chip; optionally clamps with blinking chevrons. */
  private drawPriceLine(
    ctx: CanvasRenderingContext2D,
    w: number,
    plotH: number,
    price: number,
    lo: number,
    hi: number,
    y: (v: number) => number,
    opts: { color: string; dash: number[]; label: string; chevrons: boolean },
  ): void {
    const above = price > hi;
    const below = price < lo;
    const py = above ? 14 : below ? plotH - 44 : y(price);

    // Chip geometry (verbatim from the reference): a rounded body holding the
    // label zone plus a fixed chevron zone, with a soft ~10px left-pointing
    // taper built from three arcTo corners. The dashed line stops where the
    // taper begins.
    ctx.font = FONT_CHIP;
    const textW = ctx.measureText(opts.label).width;
    const labelZone = Math.max(50, textW + 10);
    const chevronZone = 20;
    const lh = 19;
    const r = 5;
    const lineEndX = w - (labelZone + chevronZone + 13);
    const rectX = lineEndX + 10;
    const tipX = lineEndX + 1;
    const bodyW = labelZone + chevronZone;
    const top = py - lh / 2;
    const showChevrons = opts.chevrons && (above || below) && !this.reducedMotion;

    ctx.save();
    ctx.strokeStyle = opts.color;
    ctx.lineWidth = 1;
    if (opts.dash.length) ctx.setLineDash(opts.dash);
    ctx.beginPath();
    ctx.moveTo(0, Math.round(py) + 0.5);
    ctx.lineTo(lineEndX, Math.round(py) + 0.5);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(rectX + r, top + lh);
    ctx.lineTo(rectX + bodyW - r, top + lh);
    ctx.arcTo(rectX + bodyW, top + lh, rectX + bodyW, top + lh - r, r);
    ctx.lineTo(rectX + bodyW, top + r);
    ctx.arcTo(rectX + bodyW, top, rectX + bodyW - r, top, r);
    ctx.lineTo(rectX + r, top);
    ctx.arcTo(rectX, top, tipX, py, r);
    ctx.arcTo(tipX, py, rectX, top + lh, r);
    ctx.arcTo(rectX, top + lh, rectX + r, top + lh, r);
    ctx.closePath();
    ctx.fillStyle = this.pal.chipBg;
    ctx.fill();
    ctx.fillStyle = this.pal.chipInk;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // When chevrons are hidden the text centers across the whole body;
    // clamped, it centers in the label zone and the chevrons take the rest.
    ctx.fillText(opts.label, rectX + labelZone / 2 + (showChevrons ? 0 : chevronZone / 2 - 1), py + 0.5, labelZone);

    if (showChevrons) {
      const t = performance.now() - this.targetAnimT0;
      const blink = (off: number) => {
        const r = ((t + off) % CHEVRON_CYCLE_MS) / CHEVRON_CYCLE_MS;
        return r < 0.25 ? r * 4 : r < 0.75 ? 1 : Math.max(0, 1 - (r - 0.75) / 0.25);
      };
      const cx = rectX + labelZone + 2;
      const cw2 = 9;
      const chH = 3.2;
      ctx.strokeStyle = this.pal.chipInk;
      ctx.lineWidth = 1.8;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const chev = (yy: number, a: number) => {
        ctx.globalAlpha = a;
        ctx.beginPath();
        if (above) {
          ctx.moveTo(cx, yy + chH);
          ctx.lineTo(cx + cw2 / 2, yy);
          ctx.lineTo(cx + cw2, yy + chH);
        } else {
          ctx.moveTo(cx, yy);
          ctx.lineTo(cx + cw2 / 2, yy + chH);
          ctx.lineTo(cx + cw2, yy);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      };
      if (above) {
        chev(py - 4.6, blink(0));
        chev(py + 0.4, blink(CHEVRON_STAGGER_MS));
      } else {
        chev(py + 1.4, blink(0));
        chev(py - 3.6, blink(CHEVRON_STAGGER_MS));
      }
    }
    ctx.restore();
  }
}
